/**
 * SBA: the following code is in most parts and its core a slightly modified version of (14.4.2021)
 * https://github.com/manthrax/THREE-CSGMesh/ which is said at that time to be the best CSG library
 * for THREE, merged with the older API of ThreeBSP (to match the holodeck source) from
 * https://github.com/chandlerprall/ThreeCSG/blob/master/threeCSG.es6 original license MIT
 */

// Return a new CSG solid representing space in either this solid or in the
// solid `csg`. Neither this solid nor the solid `csg` are modified.
//
//     A.union(B)
//
//     +-------+            +-------+
//     |       |            |       |
//     |   A   |            |       |
//     |    +--+----+   =   |       +----+
//     +----+--+    |       +----+       |
//          |   B   |            |       |
//          |       |            |       |
//          +-------+            +-------+
//
// Return a new CSG solid representing space in this solid but not in the
// solid `csg`. Neither this solid nor the solid `csg` are modified.
//
//     A.subtract(B)
//
//     +-------+            +-------+
//     |       |            |       |
//     |   A   |            |       |
//     |    +--+----+   =   |    +--+
//     +----+--+    |       +----+
//          |   B   |
//          |       |
//          +-------+
//
// Return a new CSG solid representing space both this solid and in the
// solid `csg`. Neither this solid nor the solid `csg` are modified.
//
//     A.intersect(B)
//
//     +-------+
//     |       |
//     |   A   |
//     |    +--+----+   =   +--+
//     +----+--+    |       +--+
//          |   B   |
//          |       |
//          +-------+
//

// Temporaries used to avoid internal allocation..
// abweichend von Manthrax THREE.Vector3 verwendet
let tv0 = new THREE.Vector3()
let tv1 = new THREE.Vector3()

// abweichend vom manthrax Code rausgezogen, eh constant
const COPLANAR = 0,
    FRONT = 1,
    BACK = 2,
    SPANNING = 3;

export default class ThreeBSP {

    /**
     * gibt eine Liste von BSPs zu einem Objekt oder Array von Objekten zurück. Der Objektbaum wird
     * dabei traversiert. Falls parentMatrix nicht übergeben wird, wird die Worldmatrix des
     * Objektes genommen.
     */
    static getBSPs(o, list = [], parentMatrix = null) {
        if (Array.isArray(o)) {
            o.forEach(c => ThreeBSP.getBSPs(c, list, parentMatrix));
            return list;
        }
        let matrix = parentMatrix;
        if (matrix && o.matrix) {
            matrix = parentMatrix.clone().multiply(o.matrix);
        } else if (o.matrixWorld) {
            matrix = o.matrixWorld.clone();
        }
        if (o.bsp) {
            list.push(o.bsp);
            return list;
        }
        if (o.geometry) {
            let geometry = o.geometry;
            if (geometry.isBufferGeometry) {
                geometry = new THREE.Geometry().fromBufferGeometry(geometry);
            }
            o.bsp = new ThreeBSP(geometry, matrix);
            o.bsp.mesh = o;
            if (o.bsp.tree.plane) {
                list.push(o.bsp);
            }
            return list;
        }
        if (o.children) {
            ThreeBSP.getBSPs(o.children, list, matrix);
            return list;
        }
        return list;
    }

    constructor(geometry, matrix) {
        // Convert THREE.Geometry to ThreeBSP

        this.matrix = matrix;
        if (geometry instanceof Node) {
            this.tree = geometry;
            return this;
        }

        let polygons = [];

        if (geometry.isGeometry) {
            for (let i = 0, _length_i = geometry.faces.length; i < _length_i; i++) {
                const face = geometry.faces[i];
                const faceVertexUvs = geometry.faceVertexUvs[0][i];
                const vertices = [];

                if (face instanceof THREE.Face3) {
                    let vertex = geometry.vertices[face.a];
                    let uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[0].x, faceVertexUvs[0].y) : null;
                    vertex = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[0].clone(), uvs);
                    vertex.applyMatrix4(this.matrix);
                    vertices.push(vertex);

                    vertex = geometry.vertices[face.b];
                    uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[1].x, faceVertexUvs[1].y) : null;
                    vertex = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[2].clone(), uvs);
                    vertex.applyMatrix4(this.matrix);
                    vertices.push(vertex);

                    vertex = geometry.vertices[face.c];
                    uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[2].x, faceVertexUvs[2].y) : null;
                    vertex = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[2].clone(), uvs);
                    vertex.applyMatrix4(this.matrix);
                    vertices.push(vertex);

                } else if (face instanceof THREE.Face4) {
                    let vertex = geometry.vertices[face.a];
                    let uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[0].x, faceVertexUvs[0].y) : null;
                    vertex = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[0].clone(), uvs);
                    vertex.applyMatrix4(this.matrix);
                    vertices.push(vertex);

                    vertex = geometry.vertices[face.b];
                    uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[1].x, faceVertexUvs[1].y) : null;
                    vertex = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[1].clone(), uvs);
                    vertex.applyMatrix4(this.matrix);
                    vertices.push(vertex);

                    vertex = geometry.vertices[face.c];
                    uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[2].x, faceVertexUvs[2].y) : null;
                    vertex = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[2].clone(), uvs);
                    vertex.applyMatrix4(this.matrix);
                    vertices.push(vertex);

                    vertex = geometry.vertices[face.d];
                    uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[3].x, faceVertexUvs[3].y) : null;
                    vertex = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[3].clone(), uvs);
                    vertex.applyMatrix4(this.matrix);
                    vertices.push(vertex);

                } else {
                    throw 'Invalid face type at index ' + i;
                }

                const polygon = new Polygon(vertices,face.materialIndex);
                polygons.push(polygon);
            }

        // } else if (geometry.isBufferGeometry) {

            // der folgende Code kann funktionieren, wenn mir jemand erklärt, wie man
            // die normalMatrix einer WorldMatrix4 bestimmt, um die normalen korrekt zu drehen...

            // // Erweiterung für materialIndex ---
            // let groups = geometry.groups;
            // const groupMatches = (g,i)=>i>=g.start && i<g.start+g.count;
            // let group = null;
            // const findMaterialIndex = groups==null ? ()=>0 : (i)=>{
            //     if (group==null || !groupMatches(group,i)) {
            //         group = groups.find(groupMatches);
            //     }
            //     return group==null ? 0 : group.materialIndex;
            // };
            // // ----
            //
            // // der folgende Code ist von manthrax. Dürfte etwas Performanter sein, als
            // // BufferGeometry in Geometry umzukopieren, da inzwischen ja eh nur noch
            // // BufferGeometry unterstützt wird
            //
            // let posattr = geometry.attributes.position;
            // let normalattr = geometry.attributes.normal;
            // let uvattr = geometry.attributes.uv;
            // let colorattr = geometry.attributes.color;
            // let index;
            // if (geometry.index) {
            //     index = geometry.index.array;
            // } else {
            //     index = new Array((posattr.array.length / posattr.itemSize) | 0);
            //     for (let i = 0; i < index.length; i++) {
            //         index[i] = i;
            //     }
            // }
            // let triCount = (index.length / 3) | 0;
            // const polys = new Array(triCount);
            // for (let i = 0, pli = 0, l = index.length; i < l; i += 3, pli++) {
            //     let vertices = new Array(3);
            //     for (let j = 0; j < 3; j++) {
            //         let vi = index[i + j]
            //         let vp = vi * 3;
            //         let vt = vi * 2;
            //         let x = posattr.array[vp]
            //         let y = posattr.array[vp + 1]
            //         let z = posattr.array[vp + 2]
            //         let nx = normalattr.array[vp]
            //         let ny = normalattr.array[vp + 1]
            //         let nz = normalattr.array[vp + 2]
            //         let u = uvattr.array[vt]
            //         let v = uvattr.array[vt + 1]
            //         vertices[j] = new Vertex({
            //             x,
            //             y,
            //             z
            //         }, {
            //             x: nx,
            //             y: ny,
            //             z: nz
            //         }, {
            //             x: u,
            //             y: v,
            //             z: 0
            //         }, colorattr && {
            //             x: colorattr.array[vt],
            //             y: colorattr.array[vt + 1],
            //             z: colorattr.array[vt + 2]
            //         });
            //     }
            //     polys[pli] = new Polygon(vertices, findMaterialIndex(i));
            // }

        } else {
            console.error("Unsupported CSG input type:" + geometry.type)
        }

        this.tree = new Node(polygons);
    }

    getMaterials() {
        return Array.from(new Set(this.tree.allPolygons().map(p => p.materialIndex)));
    }


    /**
     * um die operationen hier zu verstehen: https://github.com/evanw/csg.js/
     *
     * All CSG operations are implemented in terms of two functions,
     * clipTo() and invert(), which remove parts of a BSP tree inside
     * another BSP tree and swap solid and empty space, respectively.
     * To find the union of a and b, we want to remove everything in a inside b and
     * everything in b inside a, then combine polygons from a and b into one solid:
     *
     * a.clipTo(b);
     * b.clipTo(a);
     * a.build(b.allPolygons());
     *
     * The only tricky part is handling overlapping coplanar polygons in both trees.
     * The code above keeps both copies, but we need to keep them in one tree and
     * remove them in the other tree. To remove them from b we can clip
     * the inverse of b against a.
     * The code for union now looks like this:
     *
     * a.clipTo(b);
     * b.clipTo(a);
     * b.invert();
     * b.clipTo(a);
     * b.invert();
     * a.build(b.allPolygons());
     *
     * @param other_tree
     * @return {ThreeBSP}
     */
    union(other_tree) {
        let a = this.tree.clone(),
            b = other_tree.tree.clone();

        // geprüft, entspricht manthrax
        a.clipTo(b);
        b.clipTo(a);
        b.invert();
        b.clipTo(a);
        b.invert();
        a.build(b.allPolygons());
        a = new ThreeBSP(a, this.matrix);
        return a;
    }

    sub(other_tree) {
        let a = this.tree.clone(),
            b = other_tree.tree.clone();

        // geprüft, entspricht manthrax
        a.invert();
        a.clipTo(b);
        b.clipTo(a);
        b.invert();
        b.clipTo(a);
        b.invert();
        a.build(b.allPolygons());
        a.invert();

        a = new ThreeBSP(a, this.matrix);
        return a;
    }

    intersect(other_tree) {
        let a = this.tree.clone(),
            b = other_tree.tree.clone();

        // geprüft, entspricht manthrax
        a.invert();
        b.clipTo(a);
        b.invert();
        a.clipTo(b);
        b.clipTo(a);
        a.build(b.allPolygons());
        a.invert();

        a = new ThreeBSP(a, this.matrix);
        return a;
    }

    setMaterialIndexOnAllPolygons(materialIndex) {
        const polygons = this.tree.allPolygons();
        polygons.forEach(polygon => {
            polygon.materialIndex = materialIndex;
        });
    }

    toGeometry() {
        const matrix = new THREE.Matrix4().copy(this.matrix).invert();
        const polygons = this.tree.allPolygons();
        const vertices = [];
        const normals = [];
        const uvs = [];
        polygons.sort((a, b) => (a.materialIndex || 0) - (b.materialIndex || 0));
        let materialGroup = {start: 0, count: 0, materialIndex: 0};
        const materialGroups = [materialGroup];
        polygons.forEach(polygon => {
            if (polygon.materialIndex !== materialGroup.materialIndex) {
                materialGroup = {
                    start: vertices.length / 3,
                    count: 0,
                    materialIndex: polygon.materialIndex
                };
                materialGroups.push(materialGroup);
            }
            const pv = polygon.vertices.map(v => v.clone().applyMatrix4(matrix));
            const pvLength = pv.length;
            const normal = polygon.plane.normal;
            for (let i = 2; i < pvLength; i++) {
                [pv[0], pv[i - 1], pv[i]].forEach(v => {
                    vertices.push(v.x, v.y, v.z);
                    materialGroup.count++;
                    normals.push(normal.x, normal.y, normal.z);
                    if (v.uv) {
                        uvs.push(v.uv.x, v.uv.y);
                    } else {
                        uvs.push(0, 0);
                    }
                });
            }
        });
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        g.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        if (materialGroups.length > 1) {
            materialGroups.forEach(group => g.addGroup(group.start, group.count, group.materialIndex));
        }
        g.computeVertexNormals(true);

        return g;
    }

    toMesh(material) {
        const geometry = this.toGeometry();
        const mesh = new THREE.Mesh(geometry, material);

        mesh.position.setFromMatrixPosition(this.matrix);
        mesh.rotation.setFromRotationMatrix(this.matrix);

        return mesh;
    }

    setGeometryInto(mesh) {
        if (mesh) {
            // mesh.geometry.dispose();
            mesh.geometry = this.toGeometry();
            mesh.bsp = this;
        }
    }

}

/**
 * Die Klasse Vertex entspricht nicht dem manthrax Code, sondern THREE.Vector3,
 * welcher aber gleichwertig mit dem manthrax Code sein sollte
 */
class Vertex extends THREE.Vector3 {

    constructor(x, y, z, normal, uv) {
        super(x, y, z);
        this.normal = normal || new THREE.Vector3();
        this.uv = uv || new THREE.Vector2();
    }

    clone() {
        return new Vertex(this.x, this.y, this.z, this.normal.clone(), this.uv.clone());
    }

    // Invert all orientation-specific data (e.g. vertex normal). Called when the
    // orientation of a polygon is flipped.
    flip() {
        this.normal.negate();
    }

    lerp(a, t) {
        super.lerp(a, t);
        this.normal.lerp(a.normal, t);
        this.uv.lerp(a.uv, t);
        return this;
    }

    interpolate(other, t) {
        return this.clone().lerp(other, t);
    }

}


// # class Plane

// Represents a plane in 3D space.

class Plane {

    // `Plane.EPSILON` is the tolerance used by `splitPolygon()` to decide if a
    // point is on the plane.
    static EPSILON = 1e-5;

    static fromPoints(a, b, c) {
        let n = tv0.copy(b).sub(a).cross(tv1.copy(c).sub(a)).normalize()
        return new Plane(n.clone(), n.dot(a));
    }

    constructor(normal, w) {
        this.normal = normal;
        this.w = w;
    }

    clone() {
        return new Plane(this.normal.clone(), this.w);
    }

    flip() {
        this.normal.negate();
        this.w = -this.w;
    }

    // Split `polygon` by this plane if needed, then put the polygon or polygon
    // fragments in the appropriate lists. Coplanar polygons go into either
    // `coplanarFront` or `coplanarBack` depending on their orientation with
    // respect to this plane. Polygons in front or in back of this plane go into
    // either `front` or `back`.
    splitPolygon(polygon, coplanarFront, coplanarBack, front, back) {
        // Classify each point as well as the entire polygon into one of the above
        // four classes.
        let polygonType = 0;
        let types = [];
        for (let i = 0; i < polygon.vertices.length; i++) {
            let t = this.normal.dot(polygon.vertices[i]) - this.w;
            let type = (t < -Plane.EPSILON) ? BACK : (t > Plane.EPSILON) ? FRONT : COPLANAR;
            polygonType |= type;
            types.push(type);
        }

        // Put the polygon in the correct list, splitting it when necessary.
        switch (polygonType) {
            case COPLANAR:
                (this.normal.dot(polygon.plane.normal) > 0 ? coplanarFront : coplanarBack).push(polygon);
                break;
            case FRONT:
                front.push(polygon);
                break;
            case BACK:
                back.push(polygon);
                break;
            case SPANNING:
                let f = []
                    , b = [];
                for (let i = 0; i < polygon.vertices.length; i++) {
                    let j = (i + 1) % polygon.vertices.length;
                    let ti = types[i]
                        , tj = types[j];
                    let vi = polygon.vertices[i]
                        , vj = polygon.vertices[j];
                    if (ti != BACK)
                        f.push(vi);
                    if (ti != FRONT)
                        b.push(ti != BACK ? vi.clone() : vi);
                    if ((ti | tj) == SPANNING) {
                        let t = (this.w - this.normal.dot(vi)) / this.normal.dot(tv0.copy(vj).sub(vi));
                        let v = vi.interpolate(vj, t);
                        f.push(v);
                        b.push(v.clone());
                    }
                }
                if (f.length >= 3)
                    front.push(new Polygon(f, polygon.materialIndex));
                if (b.length >= 3)
                    back.push(new Polygon(b, polygon.materialIndex));
                break;
        }
    }

}

// # class Polygon

// Represents a convex polygon. The vertices used to initialize a polygon must
// be coplanar and form a convex loop. They do not have to be `Vertex`
// instances but they must behave similarly (duck typing can be used for
// customization).
//
// Each convex polygon has a `materialIndex` property, which is materialIndex between all
// polygons that are clones of each other or were split from the same polygon.
// This can be used to define per-polygon properties (such as surface color).

class Polygon {
    constructor(vertices, materialIndex) {
        this.vertices = vertices;
        this.materialIndex = materialIndex;
        this.plane = Plane.fromPoints(vertices[0], vertices[1], vertices[2]);
    }

    clone() {
        return new Polygon(this.vertices.map(v => v.clone()), this.materialIndex);
    }

    flip() {
        this.vertices.reverse().map(v => v.flip())
        this.plane.flip();
    }
}

// # class Node

// Holds a node in a BSP tree. A BSP tree is built from a collection of polygons
// by picking a polygon to split along. That polygon (and all other coplanar
// polygons) are added directly to that node and the other polygons are added to
// the front and/or back subtrees. This is not a leafy BSP tree since there is
// no distinction between internal and leaf nodes.

class Node {

    constructor(polygons) {
        this.plane = null;
        this.front = null;
        this.back = null;
        this.polygons = [];
        if (polygons)
            this.build(polygons);
    }

    clone() {
        const node = new Node();
        node.plane = this.plane && this.plane.clone();
        node.front = this.front && this.front.clone();
        node.back = this.back && this.back.clone();
        node.polygons = this.polygons.map(p => p.clone());
        return node;
    }

    // Convert solid space to empty space and empty space to solid space.
    invert() {
        for (let i = 0; i < this.polygons.length; i++)
            this.polygons[i].flip();

        this.plane && this.plane.flip();
        this.front && this.front.invert();
        this.back && this.back.invert();
        let temp = this.front;
        this.front = this.back;
        this.back = temp;
    }

    // Recursively remove all polygons in `polygons` that are inside this BSP
    // tree.
    clipPolygons(polygons) {
        if (!this.plane)
            return polygons.slice();
        let front = []
            , back = [];
        for (let i = 0; i < polygons.length; i++) {
            this.plane.splitPolygon(polygons[i], front, back, front, back);
        }
        if (this.front)
            front = this.front.clipPolygons(front);
        if (this.back)
            back = this.back.clipPolygons(back);
        else
            back = [];
        return front.concat(back);
    }

    // Remove all polygons in this BSP tree that are inside the other BSP tree
    // `bsp`.
    clipTo(bsp) {
        this.polygons = bsp.clipPolygons(this.polygons);
        if (this.front)
            this.front.clipTo(bsp);
        if (this.back)
            this.back.clipTo(bsp);
    }

    // Return a list of all polygons in this BSP tree.
    allPolygons() {
        let polygons = this.polygons.slice();
        if (this.front)
            polygons = polygons.concat(this.front.allPolygons());
        if (this.back)
            polygons = polygons.concat(this.back.allPolygons());
        return polygons;
    }

    // Build a BSP tree out of `polygons`. When called on an existing tree, the
    // new polygons are filtered down to the bottom of the tree and become new
    // nodes there. Each set of polygons is partitioned using the first polygon
    // (no heuristic is used to pick a good split).
    build(polygons) {
        if (!polygons.length)
            return;
        if (!this.plane)
            this.plane = polygons[0].plane.clone();
        let front = []
            , back = [];
        for (let i = 0; i < polygons.length; i++) {
            this.plane.splitPolygon(polygons[i], this.polygons, this.polygons, front, back);
        }
        if (front.length) {
            if (!this.front)
                this.front = new Node();
            this.front.build(front);
        }
        if (back.length) {
            if (!this.back)
                this.back = new Node();
            this.back.build(back);
        }
    }

    // ursprünglich nicht in manthrax Version, übernommen aus der älteren ThreeBSP des holodecks
    isConvex(polygons) {
        let i, j;
        for (i = 0; i < polygons.length; i++) {
            for (j = 0; j < polygons.length; j++) {
                if (i !== j && polygons[i].classifySide(polygons[j]) !== BACK) {
                    return false;
                }
            }
        }
        return true;
    }

}

